import {
  formatMeasurementModeFallback,
  formatQuestionnaireTypeFallback,
  getMeasurementModeDefinition,
  getQuestionnaireTypeDefinition,
} from "./patientProfileData";

function normalizeOptionalString(value) {
  if (value === undefined || value === null) {
    return "";
  }

  const normalizedValue = String(value).trim();
  return normalizedValue.length > 0 ? normalizedValue : "";
}

export function getMeasurementTypeFilterValue(entry) {
  const definition = getMeasurementModeDefinition(entry);

  if (definition?.modeValue) {
    return definition.modeValue;
  }

  return normalizeOptionalString(entry?.variant || entry?.collectionName);
}

export function getMeasurementTypeFilterLabel(entry, t) {
  const definition = getMeasurementModeDefinition(entry);

  if (!definition) {
    return formatMeasurementModeFallback(entry);
  }

  return t(definition.labelKey, definition.labelDefault);
}

export function getMeasurementQualityFilterValue(value) {
  const normalizedValue = normalizeOptionalString(value).toLowerCase();

  if (!normalizedValue || normalizedValue === "n/a" || normalizedValue === "na") {
    return "na";
  }

  if (
    normalizedValue === "pass" ||
    normalizedValue === "passed" ||
    normalizedValue === "good" ||
    normalizedValue === "very good"
  ) {
    return "pass";
  }

  return "fail";
}

export function getMeasurementSignalFilterValue(entry) {
  const measurementModeValue = getMeasurementModeDefinition(entry)?.modeValue;

  if (measurementModeValue === "manual-audio") {
    return "na";
  }

  return getMeasurementQualityFilterValue(entry?.signalQualityStatus);
}

export function getMeasurementSpiroFilterValue(entry) {
  const isSpirometryMeasurement =
    getMeasurementModeDefinition(entry)?.modeValue === "spirometry";

  if (!isSpirometryMeasurement) {
    return "na";
  }

  return getMeasurementQualityFilterValue(entry?.spiroSignalQualityStatus);
}

export function getMeasurementTypeFilterOptions(entries, t) {
  const optionsByValue = new Map();

  entries.forEach((entry) => {
    const value = getMeasurementTypeFilterValue(entry);

    if (!value || optionsByValue.has(value)) {
      return;
    }

    optionsByValue.set(value, {
      value,
      label: getMeasurementTypeFilterLabel(entry, t),
    });
  });

  return [...optionsByValue.values()].sort((left, right) =>
    left.label.localeCompare(right.label),
  );
}

export function filterMeasurementEntries(entries = [], filters = {}) {
  const selectedTypes = new Set(filters.types || []);
  const selectedSignalStatuses = new Set(filters.signalStatuses || []);
  const selectedSpiroStatuses = new Set(filters.spiroStatuses || []);

  return entries.filter((entry) => {
    if (
      selectedTypes.size > 0 &&
      !selectedTypes.has(getMeasurementTypeFilterValue(entry))
    ) {
      return false;
    }

    if (
      selectedSignalStatuses.size > 0 &&
      !selectedSignalStatuses.has(getMeasurementSignalFilterValue(entry))
    ) {
      return false;
    }

    if (
      selectedSpiroStatuses.size > 0 &&
      !selectedSpiroStatuses.has(getMeasurementSpiroFilterValue(entry))
    ) {
      return false;
    }

    return true;
  });
}

export function getQuestionnaireTypeFilterValue(entryOrType) {
  const questionnaireType =
    typeof entryOrType === "string"
      ? entryOrType
      : entryOrType?.questionnaireType;

  return normalizeOptionalString(questionnaireType).toLowerCase();
}

export function getQuestionnaireTypeFilterLabel(entryOrType, t) {
  const questionnaireType =
    typeof entryOrType === "string"
      ? entryOrType
      : entryOrType?.questionnaireType;
  const definition = getQuestionnaireTypeDefinition(questionnaireType);

  if (!definition) {
    return formatQuestionnaireTypeFallback(questionnaireType);
  }

  return t(definition.labelKey, definition.labelDefault);
}

export function getQuestionnaireTypeFilterOptions(entries, t) {
  const optionsByValue = new Map();

  entries.forEach((entry) => {
    const value = getQuestionnaireTypeFilterValue(entry);

    if (!value || optionsByValue.has(value)) {
      return;
    }

    optionsByValue.set(value, {
      value,
      label: getQuestionnaireTypeFilterLabel(entry, t),
    });
  });

  return [...optionsByValue.values()].sort((left, right) =>
    left.label.localeCompare(right.label),
  );
}

export function filterQuestionnaireEntries(entries = [], filters = {}) {
  const selectedTypes = new Set(filters.types || []);

  return entries.filter((entry) => {
    if (selectedTypes.size === 0) {
      return true;
    }

    return selectedTypes.has(getQuestionnaireTypeFilterValue(entry));
  });
}
